import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery, withPrefix } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import Slider from "react-slick";
import slugify from "@sindresorhus/slugify";
import { Link } from "./link";
import useKeepScroll from "../hooks/use-keep-scroll";
import getPostPath from "../utility/get-post-path";
import { normalizeRelativePath } from "../utility/paths";
import JsonLd from "./json-ld";
import { Helmet } from "react-helmet-async";

const settings = {
  dots: true,
  autoplay: true,
  arrows: false,
  swipe: true,
  autoplaySpeed: "6000",
  slide: "box-stories",
  responsive: [
    {
      breakpoint: 9999,
      settings: "unslick",
    },
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 1,
        centerMode: true,
        centerPadding: "50px",
      },
    },
  ],
};

const generateMetadata = (blogPage, blogUrl, currentStories) => {
  if (!blogPage || !blogUrl || !currentStories) return null;
  return {
    "@context": "https://schema.org",
    "@type": "LiveBlogPosting",
    "@id": `${process.env.GATSBY_SITE_URL}${withPrefix(blogUrl)}`, //url del blog
    "headline": blogPage.title,
    ...(blogPage?.seo?.descriptionSeo && {
      description: blogPage?.seo?.descriptionSeo,
    }),
    "liveBlogUpdate": currentStories.map((story) => ({
      "@type": "BlogPosting",
      "headline": story.blogTitleLong,
      "datePublished":
        story?.modificationDateString !== "Invalid Date"
          ? story.modificationDateString
          : story.formattedDate,
      "url": `${process.env.GATSBY_SITE_URL}${withPrefix(`/${getPostPath(blogPage, story)}/`)}`,
    })),
  };
};

const Story = ({ story, blogPage, numWord = 20 }) => {
  const previewText = useMemo(() => {
    if (story.blogTextPreview && story.blogTextPreview) {
      return story.blogTextPreview;
    }
    const previewWords = story.blogContentText.split(" ");
    const resultText =
      previewWords.length > numWord
        ? previewWords.slice(0, numWord).join(" ") + "..."
        : previewWords.join(" ");
    return `<p>${resultText}</p>`;
  }, [story, numWord]);
  return (
    <Link
      to={`/${getPostPath(blogPage, story)}/`}
      className="box-stories d-flex flex-column flex-md-row"
    >
      <GatsbyImage
        className="box-stories__img"
        image={getImage(story.blogCoverImageFieldSet.blogCoverImage.node)}
        alt={story.blogCoverImageFieldSet.blogAltCoverImage || ""}
        loading="eager"
      />
      <div
        className={`box-stories__opacity${
          story.blogTitleOpacity?.[0] === "Dark" ? " box-stories__opacity--black" : ""
        }`}
      ></div>
      <h3
        className={`box-stories__title align-self-center${
          story.blogTitleColor?.[0] === "Black" ? " box-stories__title--black" : ""
        }`}
      >
        {story.blogTitleShort || story.blogTitleLong}
      </h3>
      <div
        className="box-stories__desc d-md-none"
        dangerouslySetInnerHTML={{ __html: previewText }}
      />
    </Link>
  );
};

const StoriesFilter = ({ category, allCategories, selectCategory, labelManagerLabels }) => {
  const [showFilters, setShowFilters] = useState(false);
  const toggleFilter = useCallback(() => setShowFilters((value) => !value), []);
  const currentCategory = allCategories.find((item) => item.liferayId === category);

  return (
    <div className={`category-filters${showFilters ? " category-filters--open" : ""}`}>
      <button onClick={toggleFilter} className="category-filters__toggle d-md-none">
        {currentCategory ? currentCategory.name : labelManagerLabels.blogAll}
      </button>
      <ul
        className={`category-filters__list${
          showFilters ? "" : " d-md-flex justify-content-center"
        }`}
      >
        <li className="category-filters__item" data-category="all">
          <button
            name="all"
            onClick={selectCategory}
            className={`category-filters__link${
              "all" === category ? " category-filters__link--selected" : ""
            }`}
          >
            {labelManagerLabels.blogAll}
          </button>
        </li>
        {allCategories.map((item) => (
          <li
            className="category-filters__item"
            data-category={slugify(item.name)}
            key={item.liferayId}
          >
            <button
              name={slugify(item.name)}
              onClick={selectCategory}
              className={`category-filters__link${
                slugify(item.name) === category ? " category-filters__link--selected" : ""
              }`}
            >
              {item.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const HomeStories = ({ data, location, isFirst }) => {
  // Query string
  const [query, updateQuery] = useKeepScroll(location);
  const view = data.viewBlog[0];
  const filter = view === "List Page" ? query.category : null;
  const page = useMemo(() => {
    const pageParam = view === "List Page" ? parseInt(query.page) : null;
    return pageParam && !isNaN(pageParam) ? pageParam : 1;
  }, [view, query.page]);
  const { site, blogPage, posts, allLiferayTaxonomyCategory, labelManagerLabels } =
    useStaticQuery(graphql`
      {
        site {
          siteMetadata {
            title
            siteUrl
            description
            author
            lang
          }
        }
        blogPage {
          liferayFields {
            title
          }
          slug
          fields {
            hrefLanguages
          }
          seo {
            ...SeoFragment
          }
        }
        posts: allLiferayLocaleBlog {
          id
          liferayFields {
            title
            taxonomyCategoryBriefs {
              liferayId
              name
            }
            datePublished
          }
          slug
          formattedDate
          modificationDate
          modificationDateString
          blogTitleLong
          blogTitleShort
          blogTitleColor
          blogTitleOpacity
          blogTextPreview
          blogContentText
          blogCoverImageFieldSet {
            blogCoverImage {
              node {
                gatsbyImageData(width: 800, layout: CONSTRAINED, quality: 60)
              }
            }
            blogAltCoverImage
          }
        }
        allLiferayTaxonomyCategory {
          nodes {
            name
            liferayId
            parentTaxonomyVocabulary {
              name
            }
          }
        }
        labelManagerLabels {
          blogAll
          goToBlog
          loadMore
        }
      }
    `);

  const blogConfig = useMemo(() => {
    return view === "Homepage"
      ? {
          title: data.titleHp,
          numberOfPosts: parseInt(data.homepageConfigFieldSet.numberOfPosts),
          numWordPreviewMobile: parseInt(data.homepageConfigFieldSet.numWordPreviewMobile),
        }
      : {
          title: data.titleHp,
          numberOfPosts: parseInt(data.listPageConfigFieldSet.nOfPostsListPage),
          numWordPreviewMobile: parseInt(data.listPageConfigFieldSet.nWordPreviewMobileListPage),
        };
  }, [data, view]);

  const allCategories = allLiferayTaxonomyCategory.nodes;
  const topCategories = useMemo(
    () =>
      allCategories.filter(
        (category) => category.parentTaxonomyVocabulary.name === "blog-vocabulary"
      ),
    [allCategories]
  );

  const category = useMemo(
    () =>
      view === "List Page"
        ? allCategories.find((item) => slugify(item.name) === filter)
          ? filter
          : "all"
        : "all",
    [filter, view, allCategories]
  );
  const selectCategory = useCallback(
    (e) => {
      updateQuery({
        ...(e.target.name === "all" ? null : { category: e.target.name }),
      });
    },
    [updateQuery]
  );

  const stories = useMemo(() => {
    return view === "List Page"
      ? category === "all"
        ? posts
        : posts.filter((story) =>
            story.liferayFields.taxonomyCategoryBriefs.find(
              (item) => slugify(item.name) === category
            )
          )
      : posts;
  }, [posts, view, category]);

  const currentStories = useMemo(
    () =>
      stories
        .sort(function (a, b) {
          const keyA =
            a.modificationDate !== "" ? a.modificationDate : a.liferayFields.datePublished;
          const keyB =
            b.modificationDate !== "" ? b.modificationDate : b.liferayFields.datePublished;
          if (keyA < keyB) return 1;
          if (keyA > keyB) return -1;
          return 0;
        })
        .slice(0, blogConfig.numberOfPosts + (page - 1) * 6),
    [stories, blogConfig.numberOfPosts, page]
  );

  const loadMore = useCallback(() => {
    if (currentStories.length < stories.length) {
      //updateQuery({ category: filter, page: page + 1 });
      updateQuery({
        ...(filter ? { category: filter, page: page + 1 } : { page: page + 1 }),
      });
    }
  }, [currentStories.length, stories.length, page, filter, updateQuery]);

  const blogUrl = useMemo(() => normalizeRelativePath(blogPage.slug), [blogPage.slug]);

  const jsonLdData = useMemo(
    () => generateMetadata(blogPage, blogUrl, currentStories),
    [blogPage, blogUrl, currentStories]
  );

  const next = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const currentPage = params.get("page") || 1;
    return currentStories.length < stories.length ? Number(currentPage) + 1 : null;
  }, [location.search, currentStories.length, stories.length]);

  const prev = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const currentPage = params.get("page") || 1;
    return currentPage > 1 ? Number(currentPage) - 1 : null;
  }, [location.search]);

  if (!blogConfig.numberOfPosts) {
    return null;
  }

  return (
    <section className="section haier-stories">
      <Helmet>
        {next && (
          <link
            rel="next"
            href={`${site.siteMetadata.siteUrl}${withPrefix(normalizeRelativePath(blogPage.slug))}${
              !!category && category !== "all" ? `?category=${category}&` : "?"
            }page=${next}`}
          ></link>
        )}
        {!!prev && prev !== 1 ? (
          <link
            rel="prev"
            href={`${site.siteMetadata.siteUrl}${withPrefix(normalizeRelativePath(blogPage.slug))}${
              !!category && category !== "all" ? `?category=${category}&` : "?"
            }page=${prev}`}
          ></link>
        ) : !!prev && prev === 1 ? (
          <link
            rel="prev"
            href={`${site.siteMetadata.siteUrl}${withPrefix(normalizeRelativePath(blogPage.slug))}${
              !!category && category !== "all" ? `?category=${category}` : ""
            }`}
          ></link>
        ) : null}
      </Helmet>
      <JsonLd data={jsonLdData} />
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {view === "List Page" ? (
              <StoriesFilter
                category={category}
                allCategories={topCategories}
                selectCategory={selectCategory}
                labelManagerLabels={labelManagerLabels}
              />
            ) : isFirst && blogConfig?.title ? (
              <h1 className="section__title">{blogConfig.title}</h1>
            ) : blogConfig?.title ? (
              <h2 className="section__title">{blogConfig.title}</h2>
            ) : null}
          </div>
        </div>
        <div className="row">
          <div className="col-12 px-0">
            {view === "Homepage" ? (
              <Slider {...settings} className="haier-stories__grid slider-haier-stories">
                {currentStories.map((item, i) => (
                  <Story
                    story={item}
                    blogPage={blogPage}
                    numWord={blogConfig.numWordPreviewMobile}
                    key={i}
                  />
                ))}
              </Slider>
            ) : (
              <div className="haier-stories__grid">
                {currentStories.map((item, i) => (
                  <Story
                    story={item}
                    blogPage={blogPage}
                    numWord={blogConfig.numWordPreviewMobile}
                    key={i}
                  />
                ))}
              </div>
            )}
          </div>
          {view === "Homepage" ? (
            <Link className="haier-stories__link" to={blogUrl}>
              {labelManagerLabels.goToBlog}
            </Link>
          ) : (
            currentStories.length < stories.length && (
              <button className="haier-stories__link" onClick={loadMore}>
                {labelManagerLabels.loadMore}
              </button>
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default HomeStories;
export const fragment = graphql`
  fragment BlogPageFragment on LiferayBlogPage {
    id
    liferayFields {
      siteId
      articleId
    }
    titleHp
    homepageConfigFieldSet {
      numberOfPosts
      numWordPreviewMobile
    }
    viewBlog
    fields {
      hrefLanguages
    }
    listPageConfigFieldSet {
      nOfPostsListPage
      nWordPreviewMobileListPage
    }
  }
`;

HomeStories.defaultProps = {
  view: "Homepage",
};

HomeStories.propTypes = {
  view: PropTypes.oneOf(["Homepage", "List Page"]),
};
